import { requestCache } from "src/config/requestCache";
import axios, {
  AxiosError,
  AxiosInstance,
  InternalAxiosRequestConfig,
  AxiosRequestConfig,
  AxiosResponse,
} from "axios";

interface RetryableRequestConfig extends InternalAxiosRequestConfig {
  sessionRefreshAttempted?: boolean;
}

class API {
  static url: string =
    (typeof window !== "undefined" && (window as any)?.env?.API_URI) ||
    (globalThis?.process?.env?.API_URI as string) ||
    "http://localhost:7777";
  static codesNeedLogout: number[] = [400, 401, 402, 403, 404, 424, 429];

  private api!: AxiosInstance;
  private initialized = false;

  private static refreshInProgress: Promise<void> | null = null;
  private static csrfToken: string | null = null;

  static setCsrfToken(token: string | null): void {
    API.csrfToken = token;
  }

  createAPIInstance(): void {
    if (this.initialized) return;
    this.initialized = true;
    this.api = axios.create({
      baseURL: API.url,
      headers: { Accept: "application/json" },
      withCredentials: true,
    });

    this.api.defaults.timeout = 60000;
    this.api.interceptors.request.use((config) => {
      if (
        API.csrfToken &&
        ["post", "put", "patch", "delete"].includes(
          (config.method || "get").toLowerCase(),
        )
      ) {
        config.headers.set("X-CSRF-Token", API.csrfToken);
      }
      return config;
    });
    this.api.interceptors.response.use(
      (response) => {
        if (
          ["post", "put", "patch", "delete"].includes(
            (response.config.method ?? "get").toLowerCase(),
          )
        )
          requestCache.clear();
        return response;
      },
      async (error: AxiosError) => {
        const config = error.config as RetryableRequestConfig | undefined;
        if (
          error.response?.status !== 401 ||
          !config ||
          config.sessionRefreshAttempted ||
          config.url?.includes("/auth/login") ||
          config.url?.includes("/auth/refresh")
        ) {
          return Promise.reject(error);
        }

        config.sessionRefreshAttempted = true;
        try {
          API.refreshInProgress ??= this.api
            .post("/auth/refresh")
            .then(() => undefined)
            .finally(() => {
              API.refreshInProgress = null;
            });
          await API.refreshInProgress;
          return await this.api.request(config);
        } catch {
          return Promise.reject(error);
        }
      },
    );
  }

  createAPI(): void {
    this.createAPIInstance();
  }

  isCodeNeedsLogout(status: number): boolean {
    return API.codesNeedLogout.includes(status);
  }

  get<T = any>(
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> {
    return this.api.get<T>(url, config);
  }

  post<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> {
    return this.api.post<T>(url, data, config);
  }

  put<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> {
    return this.api.put<T>(url, data, config);
  }

  delete<T = any>(
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> {
    return this.api.delete<T>(url, config);
  }
}

export default API;
